import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { BrandColors } from "@/config/themes/brandColors";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * The tenant's own branding — `GET /branding`, changed through `/branding/logo` and
 * `/branding/colors`. `null` on any field means nothing is configured for that part and Olive's
 * default shows instead.
 *
 * The image URLs are short-lived presigned links into the tenant's S3 bucket, minted on every read,
 * so they are always taken from the latest response rather than kept anywhere.
 */
export type TenantBranding = {
  brand_name: string | null;
  /** The square mark: collapsed sidebar and browser tab, and beside the text name when expanded. */
  icon_url: string | null;
  /** Icon and name in one image, shown on its own in the expanded sidebar when present. */
  full_logo_url: string | null;
  /** `#rrggbb` seeds the whole themed palette is derived from. Both or neither. */
  primary_color: string | null;
  secondary_color: string | null;
};

export const brandingEmpty: TenantBranding = {
  brand_name: null,
  icon_url: null,
  full_logo_url: null,
  primary_color: null,
  secondary_color: null,
};

export type BrandingState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  branding: TenantBranding;
};

export const brandingInitialState: BrandingState = {
  status: STATUS.IDLE,
  error: null,
  branding: brandingEmpty,
};

/** What a save does to one image: upload `file`, drop the saved one (`remove`), or neither. */
export type BrandingImageUpdate = {
  file: File | null;
  remove: boolean;
};

export type SaveBrandingLogoPayload = {
  brandName: string;
  icon: BrandingImageUpdate;
  fullLogo: BrandingImageUpdate;
};

function isNullableString(value: unknown): value is string | null {
  return typeof value === "string" || value === null;
}

function isTenantBranding(value: unknown): value is TenantBranding {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    isNullableString(row.brand_name) &&
    isNullableString(row.icon_url) &&
    isNullableString(row.full_logo_url) &&
    isNullableString(row.primary_color) &&
    isNullableString(row.secondary_color)
  );
}

/** The branding a response carries, or the message to reject with. */
async function readBrandingResponse(res: Response): Promise<TenantBranding | string> {
  if (!res.ok) {
    return readApiErrorMessage(res);
  }
  const data: unknown = await res.json();
  return isTenantBranding(data) ? data : "Unexpected response shape";
}

function appendImageUpdate(body: FormData, field: string, update: BrandingImageUpdate) {
  body.append(`remove_${field}`, String(update.remove));
  // Left out entirely when nothing was picked, which is what tells the backend to keep the image.
  if (update.file !== null) {
    body.append(field, update.file);
  }
}

export const fetchBranding = createAsyncThunk<TenantBranding, void, { rejectValue: string }>(
  "branding/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/branding", { method: "GET" });
      const result = await readBrandingResponse(res);
      return typeof result === "string" ? rejectWithValue(result) : result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const saveBrandingLogo = createAsyncThunk<
  TenantBranding,
  SaveBrandingLogoPayload,
  { rejectValue: string }
>("branding/saveLogo", async (payload, { rejectWithValue }) => {
  const body = new FormData();
  body.append("brand_name", payload.brandName);
  appendImageUpdate(body, "icon", payload.icon);
  appendImageUpdate(body, "full_logo", payload.fullLogo);
  try {
    const res = await apiFetch("/v1/branding/logo", { method: "PUT", body });
    const result = await readBrandingResponse(res);
    return typeof result === "string" ? rejectWithValue(result) : result;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/** Back to Olive's icon and name. The brand colours are a separate setting and stay. */
export const resetBrandingLogo = createAsyncThunk<TenantBranding, void, { rejectValue: string }>(
  "branding/resetLogo",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/branding/logo", { method: "DELETE" });
      const result = await readBrandingResponse(res);
      return typeof result === "string" ? rejectWithValue(result) : result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

/** Both brand colours, or `null` to go back to Olive's own theme. */
export const saveBrandingColors = createAsyncThunk<
  TenantBranding,
  BrandColors | null,
  { rejectValue: string }
>("branding/saveColors", async (colors, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/branding/colors", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        primary_color: colors?.primary ?? null,
        secondary_color: colors?.secondary ?? null,
      }),
    });
    const result = await readBrandingResponse(res);
    return typeof result === "string" ? rejectWithValue(result) : result;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const brandingSlice = createSlice({
  name: "branding",
  initialState: brandingInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchBranding.pending, (state) => {
        // A refetch keeps showing what it already has rather than flashing a loader: the sidebar
        // and the settings form both read this, and only the first load has nothing to show.
        if (state.status !== STATUS.SUCCEEDED) {
          state.status = STATUS.LOADING;
        }
        state.error = null;
      })
      .addCase(fetchBranding.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.branding = action.payload;
      })
      .addCase(fetchBranding.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      // Save and reset errors are the form's to show, not a load failure, so they leave `status`
      // alone. Success replaces the branding, which is what repaints the sidebar, the tab icon and
      // the theme.
      .addCase(saveBrandingLogo.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.branding = action.payload;
      })
      .addCase(resetBrandingLogo.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.branding = action.payload;
      })
      .addCase(saveBrandingColors.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.branding = action.payload;
      });
  },
});

export const brandingReducer = brandingSlice.reducer;
